import React, { useCallback, useRef } from 'react';

import BaseFlow from '../components/BaseFlow';
import { pushHistory, removeHistory, replaceHistory } from '../redux/history';
import { useAppDispatch } from '../redux/hooks';
import type { PageChangeEvent, PageComponent, PageHandle } from '../types/BaseFlow';

type MasterFlowProps = {
  finish: (url?: string) => void;
  onPageChange?: (page: PageChangeEvent) => void;
  pages: PageComponent[];
};

/**
 * Master Flow
 */
const MasterFlow = ({ finish, onPageChange, pages }: MasterFlowProps) => {
  const dispatch = useAppDispatch();
  const flowRef = useRef(null);

  const handlePageChange = useCallback(
    (page: PageChangeEvent) => {
      // Only publish changes out for viewable pages (not flows)
      if (page?.viewable) {
        // Replace the previous page in the history to catch changes to backable/replayable flags
        if (page.previousPage?.viewable) {
          dispatch(replaceHistory(page.previousPage));
        }

        // Push the new page into the history
        dispatch(pushHistory(page));

        // Bubble up page change events from here
        onPageChange?.(page);
      }
    },
    [dispatch, onPageChange],
  );

  const onPrevious = useCallback(
    (page: PageHandle | null) => {
      // Remove the page from the history
      if (page?.viewable) {
        dispatch(removeHistory(page));
      }
    },
    [dispatch],
  );

  return (
    <BaseFlow
      className="master-flow"
      finish={finish}
      key={MasterFlow.displayName}
      name={MasterFlow.displayName}
      next={finish} // Trigger a finish when this flow is done
      onPageChange={handlePageChange}
      onPrevious={onPrevious}
      pages={pages}
      ref={flowRef}
    />
  );
};

MasterFlow.displayName = 'MasterFlow';

export default MasterFlow;
